import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import React from "react";

import { useAuth } from "@wc/domain/authn";
import { deleteAccount } from "@wc/domain/users";

/**
 * Modal for confirming account deletion
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {() => void} props.onClose Callback when modal is closed
 * @returns {React.ReactElement} The delete account modal
 */
export default function DeleteAccountModal({ isOpen, onClose }) {
  const auth = useAuth();
  const user = auth.getCurrentUser();
  const [confirmation, setConfirmation] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const expectedConfirmation = "delete my account";
  const canDelete = confirmation.toLowerCase() === expectedConfirmation;

  const handleDelete = async () => {
    if (!canDelete) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteAccount(user.id);
      await auth.logout();
    } catch (err) {
      console.error("Failed to delete account:", err);
      setError("Failed to delete account. Please try again.");
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setConfirmation("");
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalHeader className="text-danger flex flex-col gap-1">
          Delete Account
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p>
              This action <strong>cannot be undone</strong>. This will
              permanently delete your account and remove your data.
            </p>
            <p className="text-default-500 text-sm">
              If you are the only member of any organizations, those
              organizations and their teams will also be deleted. If you are the
              only admin, another member will be promoted to admin.
            </p>
            <p className="text-sm">
              Please type <strong>{expectedConfirmation}</strong> to confirm.
            </p>
            <Input
              autoFocus
              placeholder={expectedConfirmation}
              value={confirmation}
              onValueChange={setConfirmation}
              isDisabled={isDeleting}
              isInvalid={!!error}
              errorMessage={error}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="danger"
            onPress={handleDelete}
            isLoading={isDeleting}
            isDisabled={!canDelete || isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Account"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
